import { useState, type FormEvent } from 'react';
import { Button, DialogActions, DialogContent, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Settings } from '../../types';
import { SETTINGS_TAB_LABELS as LABELS, DETAIL_PANEL_LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';

interface ReturnEmailTemplateModalProps {
  settings: Settings;
  onClose: () => void;
  onSave: (values: Pick<Settings, 'return_email_subject' | 'return_email_body'>) => Promise<boolean>;
}

export default function ReturnEmailTemplateModal({ settings, onClose, onSave }: ReturnEmailTemplateModalProps) {
  useModalHistory(onClose);
  const [subject, setSubject] = useState(settings.return_email_subject ?? DETAIL_PANEL_LABELS.returnEmailSubject);
  const [body, setBody] = useState(settings.return_email_body ?? DETAIL_PANEL_LABELS.returnEmailBody);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const ok = await onSave({ return_email_subject: subject, return_email_body: body });
    setSaving(false);
    if (ok) onClose();
  };

  const handleReset = async () => {
    setResetting(true);
    const ok = await onSave({ return_email_subject: null, return_email_body: null });
    setResetting(false);
    if (ok) {
      setSubject(DETAIL_PANEL_LABELS.returnEmailSubject);
      setBody(DETAIL_PANEL_LABELS.returnEmailBody);
      onClose();
    }
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={LABELS.returnEmailTemplateModalTitle}
      confirmLabel={LABELS.saveButton}
      confirmFormId="return-email-template-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="return-email-template-modal"
    >
      <form id="return-email-template-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <div className="flex items-center gap-1">
            <p className="text-xs text-text-secondary mb-0 mt-0">{LABELS.returnEmailTemplateSubtitle}</p>
            <Tooltip title={LABELS.returnEmailVariablesInfo} arrow>
              <InfoOutlinedIcon
                fontSize="small"
                className="text-text-secondary shrink-0"
                aria-label={LABELS.returnEmailVariablesInfo}
              />
            </Tooltip>
          </div>
          <TextField
            label={LABELS.returnEmailSubjectLabel}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
          />
          <TextField
            label={LABELS.returnEmailBodyLabel}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            rows={12}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton onClick={handleReset} color="inherit" loading={resetting} data-testid="reset-button">
            {LABELS.returnEmailResetButton}
          </SpinnerButton>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.saveButton}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
